import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, DeprecatedIconButton, Popover, Switch } from "@octopusdeploy/design-system-components";
import { InfoCircleIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ProjectLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useEffect, useRef } from "react";
import type { LiveStatusSwitchDispatcher, LiveStatusSwitchEvent } from "~/analytics/Analytics";
import { useAnalyticLiveStatusSwitchDispatch } from "~/analytics/Analytics";
import { KubernetesAgentTargetMonitorUpgradeDialog } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/MonitorUpgrade/KubernetesAgentTargetMonitorUpgradeDialog";
import { LiveStatusConnectionIcon } from "~/areas/projects/components/Observability/LiveStatusConnectionIcon";
import InternalLink from "~/components/Navigation/InternalLink";
export type LiveStatusSwitchProps = {
    value: boolean;
    onValueChange: (newValue: boolean) => void;
    showLiveStatusInfoIconButton: boolean;
    accessibleName: string;
    spaceId: string;
    analyticEventData: Omit<LiveStatusSwitchEvent, "switchEnabled">;
};
export function LiveStatusSwitch(props: LiveStatusSwitchProps) {
    const { value, onValueChange, accessibleName, spaceId, analyticEventData: partialAnalyticEventData, showLiveStatusInfoIconButton } = props;
    const dispatch = useAnalyticLiveStatusSwitchDispatch();
    const toggleValue = (newValue: boolean) => {
        onValueChange(newValue);
        dispatchLiveStatusSwitchEvent(dispatch, "toggle", { ...partialAnalyticEventData, switchEnabled: newValue });
    };
    const analyticEventData = {
        ...partialAnalyticEventData,
        switchEnabled: value,
    };
    return (<span className={styles.container}>
            <Switch key="show-live-status" value={value} onChange={toggleValue} accessibleName={accessibleName}/>
            <span className={styles.label}>
                <LiveStatusConnectionIcon />
                Live status
                {showLiveStatusInfoIconButton && <LiveStatusInfoIconButton spaceId={spaceId} analyticEventData={analyticEventData}/>}
            </span>
            <LiveStatusSwitchAnalyticView eventData={analyticEventData}/>
        </span>);
}
const styles = {
    container: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.xSmall,
        lineHeight: lineHeight.xSmall,
        fontSize: fontSize.medium,
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        paddingRight: "4px",
    }),
    label: css({
        display: "flex",
        flexDirection: "row",
        gap: space[4],
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    }),
};
type LiveStatusInfoIconButtonProps = {
    spaceId: string;
    analyticEventData: LiveStatusSwitchEvent;
};
function LiveStatusInfoIconButton({ spaceId, analyticEventData }: LiveStatusInfoIconButtonProps) {
    const dispatch = useAnalyticLiveStatusSwitchDispatch();
    const infoRef = useRef<HTMLDivElement | null>(null);
    const [buttonAnchor, setButtonAnchor] = React.useState<HTMLElement | null>(null);
    const [openDialog, setOpenDialog] = React.useState(false);
    const handleClick = React.useCallback((e: React.MouseEvent) => {
        if (infoRef)
            setButtonAnchor(infoRef.current);
        dispatchLiveStatusSwitchEvent(dispatch, "info", analyticEventData);
    }, [analyticEventData, dispatch]);
    return (<div className={infoStyles.container} ref={infoRef}>
            {/* Should use InfoCircleColourIcon but it's broken */}
            <DeprecatedIconButton customIcon={<InfoCircleIcon size={16}/>} onClick={handleClick}/>

            <Popover placement={"right-start"} anchorEl={buttonAnchor} open={!!buttonAnchor} onClose={() => setButtonAnchor(null)}>
                <div className={infoStyles.popover}>
                    <div>
                        <div className={infoStyles.title}>Get live status for Kubernetes</div>
                        <p>To start getting live status, you’ll need a Kubernetes agent that supports the Octopus Kubernetes Monitor.</p>
                        <div className={infoStyles.subTitle}>Upgrade existing Kubernetes agents</div>
                        <p>You can manually upgrade your existing agents via Helm:</p>
                        <ActionButton type={ActionButtonType.Primary} label={"Generate Helm Command"} onClick={() => {
            // Close the popover to avoid it rendering over the dialog
            setButtonAnchor(null);
            setOpenDialog(true);
        }} title={"Generate Helm Command"}/>
                    </div>

                    <div>
                        <div className={infoStyles.subTitle}>Not using the Kubernetes agent yet?</div>
                        <p>
                            <InternalLink to={links.selectDeploymentTargetTypePage.generateUrl({ spaceId })} openInSelf={false} showOpensInNewTabIcon={true}>
                                Install the latest version
                            </InternalLink>{" "}
                            of the agent in your cluster.
                        </p>
                    </div>
                </div>
            </Popover>
            <KubernetesAgentTargetMonitorUpgradeDialog spaceId={spaceId} open={openDialog} closeDialog={() => setOpenDialog(false)}/>
        </div>);
}
const infoStyles = {
    container: css({ paddingTop: space[4], paddingBottom: space[4] }),
    popover: css({
        maxWidth: "328px",
        padding: space[24],
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    title: css({ font: text.body.bold.large }),
    subTitle: css({ font: text.body.bold.medium }),
};
export function liveStatusLocalStorageKey(spaceId: string) {
    return `Octopus.Dashboard.ShowLiveStatus[${spaceId}]`;
}
export function projectSupportsLiveStatus(projectLiveStatus: ProjectLiveStatusResource): boolean {
    return (projectLiveStatus?.Environments?.length || 0) > 0;
}
function LiveStatusSwitchAnalyticView({ eventData }: {
    eventData: LiveStatusSwitchEvent;
}) {
    const dispatch = useAnalyticLiveStatusSwitchDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatchLiveStatusSwitchEvent(dispatch, "view", eventData), []);
    return <></>;
}
function dispatchLiveStatusSwitchEvent(dispatch: LiveStatusSwitchDispatcher, action: "view" | "toggle" | "info", event: LiveStatusSwitchEvent) {
    switch (action) {
        case "view":
            dispatch("View Live Status Switch", event);
            break;
        case "toggle":
            dispatch("Toggle Live Status Switch", event);
            break;
        case "info":
            dispatch("View Live Status Switch Info", event);
            break;
    }
}
